import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, MapPin, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { doc, deleteDoc } from 'firebase/firestore';

import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { createOrder } from '../services/orderService';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const { items, subtotal, shipping, tax, total } = location.state || {};

  const [address, setAddress] = useState({
    name: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    pincode: '',
    landmark: '',
  });

  const [orderPlaced, setOrderPlaced] = useState(false);
  const [loading, setLoading] = useState(false);

  /* -------------------- Place Order -------------------- */
  const handlePlaceOrder = async () => {
    if (!user) return;

    // Validate address
    if (
      !address.name ||
      !address.phone ||
      !address.street ||
      !address.city ||
      !address.state ||
      !address.pincode
    ) {
      alert('Please fill all required fields');
      return;
    }

    setLoading(true);

    try {
      const orderIds = await createOrder(
        user.id,
        user.name,
        user.email,
        items,
        address,
        total
      );

      // Clear cart after successful order
      await deleteDoc(doc(db, 'carts', user.id));

      setOrderPlaced(true);

      setTimeout(() => {
        navigate('/orders');
      }, 3000);
    } catch (error) {
      console.error('Error placing order:', error);
      alert('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- Success Screen -------------------- */
  if (orderPlaced) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 p-4">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.5 }}
          className="text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-32 h-32 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle size={64} className="text-white" />
          </motion.div>

          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Order Placed!
          </h2>
          <p className="text-gray-600 mb-4">
            Your order has been successfully placed
          </p>
          <p className="text-sm text-gray-500">
            Redirecting to orders...
          </p>
        </motion.div>
      </div>
    );
  }

  /* -------------------- Checkout UI -------------------- */
  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 flex items-center gap-4 sticky top-0 z-10">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft size={24} />
        </motion.button>
        <h1 className="text-xl font-bold text-gray-800">Checkout</h1>
      </div>

      <div className="p-4 max-w-2xl mx-auto">
        {/* Delivery Address */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-white rounded-xl p-6 shadow-md mb-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="text-primary" size={24} />
            <h2 className="text-xl font-bold text-gray-800">
              Delivery Address
            </h2>
          </div>

          <div className="space-y-4">
            <Input label="Full Name *" value={address.name}
              onChange={e => setAddress({ ...address, name: e.target.value })} />

            <Input label="Phone Number *" type="tel" value={address.phone}
              onChange={e => setAddress({ ...address, phone: e.target.value })} />

            <Input label="Street Address *" value={address.street}
              onChange={e => setAddress({ ...address, street: e.target.value })} />

            <div className="grid grid-cols-2 gap-3">
              <Input label="City *" value={address.city}
                onChange={e => setAddress({ ...address, city: e.target.value })} />
              <Input label="State *" value={address.state}
                onChange={e => setAddress({ ...address, state: e.target.value })} />
            </div>

            <Input label="Pincode *" value={address.pincode}
              onChange={e => setAddress({ ...address, pincode: e.target.value })} />

            <Input label="Landmark (Optional)" value={address.landmark}
              onChange={e => setAddress({ ...address, landmark: e.target.value })} />
          </div>
        </motion.div>

        {/* Order Summary */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-md mb-6"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-4">Order Summary</h2>

          <div className="space-y-3">
            <div className="flex justify-between text-gray-600">
              <span>Items ({items?.length})</span>
              <span>₹{subtotal?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Shipping</span>
              <span>₹{shipping}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Tax</span>
              <span>₹{tax}</span>
            </div>
            <div className="border-t pt-3 flex justify-between text-lg font-bold text-gray-800">
              <span>Total</span>
              <span className="text-primary">
                ₹{total?.toLocaleString()}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Place Order Button */}
        <motion.div>
          <Button
            onClick={handlePlaceOrder}
            fullWidth
            className="shadow-lg py-4 text-lg"
            disabled={loading}
          >
            {loading
              ? 'Processing...'
              : `Place Order - ₹${total?.toLocaleString()}`}
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default Checkout;
